import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight } from 'lucide-react';

/* Props: { audience, searchParams, onSuccess }
 *   onSuccess — called when API call succeeds; parent handles success screen
 */
const LinkedInAdsForm = ({ audience, searchParams, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    companyName: '',
    audienceType: audience === 'smb' || audience === 'vendor' ? audience : '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const utmParams = (() => {
    const params = {
      utmSource: searchParams.get('utm_source'),
      utmMedium: searchParams.get('utm_medium'),
      utmCampaign: searchParams.get('utm_campaign'),
    };
    Object.keys(params).forEach(key => {
      if (!params[key]) delete params[key];
    });
    return params;
  })();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        companyName: formData.companyName,
        name: formData.name,
        email: formData.email,
        type: formData.audienceType,
        ...utmParams,
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || t('linkedin.form.errorGeneric'));
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="signup-form" className="pb-20 px-4 sm:px-6 lg:px-8 scroll-mt-24">
      <div className="max-w-xl mx-auto">
        <div className="bg-background border border-border rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-foreground mb-2 text-center">
            {t('linkedin.form.title')}
          </h2>
          <p className="text-muted-foreground mb-8 text-center">
            {t('linkedin.form.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name">{t('linkedin.form.name')}</Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder={t('linkedin.form.namePlaceholder')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="email">{t('linkedin.form.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder={t('linkedin.form.emailPlaceholder')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="companyName">{t('linkedin.form.companyName')}</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                required
                value={formData.companyName}
                onChange={handleChange}
                placeholder={t('linkedin.form.companyNamePlaceholder')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="audienceType">{t('linkedin.form.audienceType')}</Label>
              <select
                id="audienceType"
                name="audienceType"
                required
                value={formData.audienceType}
                onChange={handleChange}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">{t('linkedin.form.audienceTypePlaceholder')}</option>
                <option value="smb">{t('linkedin.form.audienceTypeSMB')}</option>
                <option value="vendor">{t('linkedin.form.audienceTypeVendor')}</option>
              </select>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              onClick={() => {
                if (window.posthog && !loading) {
                  window.posthog.capture('linkedin_cta_clicked', {
                    cta_position: 'bottom',
                    segment: audience,
                    lang: i18n.language,
                    utm_source: searchParams.get('utm_source') || undefined,
                    utm_medium: searchParams.get('utm_medium') || undefined,
                    utm_campaign: searchParams.get('utm_campaign') || undefined,
                  });
                }
              }}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-6 group"
            >
              {loading ? t('linkedin.form.submitting') : t(
                audience === 'smb' ? 'linkedin.form.ctaSMB' :
                audience === 'vendor' ? 'linkedin.form.ctaVendor' :
                'linkedin.form.cta'
              )}
              {!loading && <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              {t('linkedin.form.disclaimer')}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default LinkedInAdsForm;
